import { useEffect, useRef } from 'react';

import { ChatMessage } from './ChatMessage';
import { SystemMessage } from './SystemMessage';

import type { ClientChatMessageInfo } from '../hooks/useChatNode';
import type { SocketResponse } from '@lemoncloud/eureka-sockets-api';

interface MessageListProps {
    messages: SocketResponse<ClientChatMessageInfo>[];
    currentUserId: string;
    currentUserNickname: string;
}

/**
 * Message list container with auto-scroll functionality
 * Renders chat messages and system messages with proper scrolling behavior
 * @param {MessageListProps} props - Component props
 * @param {SocketResponse<ClientChatMessageInfo>[]} props.messages - Array of messages from WebSocket
 * @param {string} props.currentUserId - Current user's connection ID
 * @param {string} props.currentUserNickname - Current user's nickname
 * @returns {JSX.Element} Scrollable message list container
 */
export const MessageList = ({ messages, currentUserId, currentUserNickname }: MessageListProps) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom when new messages arrive
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    if (messages.length === 0) {
        return (
            <div className="h-full overflow-y-auto overflow-x-hidden px-4 pt-16 pb-20">
                <div className="text-center text-[#53555B] mt-8">
                    <p>메시지가 없습니다. 대화를 시작해보세요!</p>
                </div>
            </div>
        );
    }

    return (
        <div className="h-full overflow-y-auto overflow-x-hidden px-4 pt-16 pb-20">
            {messages.map((msg, idx) => {
                if (msg.data?.isSystemMessage) {
                    return <SystemMessage key={idx} message={msg.data.message || ''} />;
                }

                const isMyMessage = msg.data?.author === currentUserId;

                return (
                    <ChatMessage
                        key={idx}
                        message={msg.data?.message || ''}
                        timestamp={msg.ts}
                        isMyMessage={isMyMessage}
                        authorNickname={isMyMessage ? currentUserNickname : msg.data?.authorName || '익명'}
                    />
                );
            })}
            <div ref={messagesEndRef} />
        </div>
    );
};
